import { FC, useMemo, useCallback, memo } from 'react'
import { CategoryId } from 'interfaces/meta'
import { MoneyflowType } from 'interfaces/moneyflow'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { trackUIEventAction } from 'actions/analyticsActions'
import { metaSelector } from 'selectors/globalSelectors'
import { saturateCategory } from 'helpers/categoriesHelpers'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import Button from 'components/features/pfm/Button/Button'
import { AnalyticsEvent } from 'interfaces/analytics'
import { Timeframe } from 'interfaces/chart'
import { useSelectedCategoryId } from 'helpers/hookHelpers'

export type EditTrackerButtonProps = {
  type: MoneyflowType
  categoryId: CategoryId
  timeframe: Timeframe
}

export const EditTrackerButton: FC<EditTrackerButtonProps> = memo((props) => {
  const {
    type,
    categoryId,
    timeframe,
  } = props

  const dispatch = useStoreDispatch()
  const selectedCategoryId = useSelectedCategoryId(props.type)
  const meta = useStoreSelector(metaSelector)

  const handleOpenTrackerPane = useCallback<(category: CategoryId) => void>((id) => {
    dispatch(
      modifyFloatingPaneActionCreator('tracker', {
        type: 'tracker',
        payload: {
          category: id,
          type,
          timeframe,
        },
      })
    )

    const analyticsAction: AnalyticsEvent = type === 'spending'
      ? {
        category: 'page_controls-spending',
        action: 'click-tracker_cta',
        label: categoryId,
      }
      : {
        category: 'page_controls-income',
        action: 'click-tracker_cta',
        label: categoryId,
      }

    dispatch(trackUIEventAction(analyticsAction))
  }, [dispatch, type, timeframe, categoryId])

  const selectedCategory = useMemo(() => {
    return saturateCategory(selectedCategoryId, meta)
  }, [meta, selectedCategoryId])

  const handleGetCaption = useCallback(() => {
    const id = selectedCategory ? selectedCategory.id : null
    if (id === ALL_CATEGORY_ID) {
      return 'Total'
    } else if (selectedCategory) {
      return selectedCategory.value
    } else {
      return ''
    }
  }, [selectedCategory])

  return (
    <Button
      onClick={ () => handleOpenTrackerPane(categoryId) }
      theme='blue'
      size='medium'
      fullWidth
    >
      Track { handleGetCaption() } { type === 'spending' ? 'Spending' : 'Income' }
    </Button>
  )
})
